import React, { useEffect, useRef, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { toast } from 'sonner';
import { api } from './api/client.js';
import { Toaster } from './components/ui/sonner.js';
import { AppLayout } from './components/AppLayout.js';
import { PermissionDeniedPage } from './components/PermissionDeniedPage.js';
import { RouteErrorBoundary } from './components/RouteErrorBoundary.js';
import { DuplicateSessionOverlay } from './components/DuplicateSessionOverlay.js';
import { setDuplicateSessionBlock } from './duplicate-session-store.js';
import {
  getJnHoldBlock,
  setJnHoldBlock,
  subscribeJnHoldBlock,
  type JnHoldBlock,
} from './jn-hold-block-store.js';
import { JnHoldPopup } from './screens/JnHoldPopup.js';
import { GenesysReconnectBanner } from './components/GenesysReconnectBanner.js';
import {
  getReconnectNeeded,
  setReconnectNeeded,
} from './genesys-reconnect-store.js';
import { clearAllSecurityRunSnapshots } from './security-run-snapshot.js';
import { clearAllRegistrationAnswers } from './lib/registration-ipw.js';
import {
  CustomerProvider,
  useCustomer,
  resetIdentityPreservingOnlineJourney,
} from './customer-context.js';
import { AgentTellerProvider } from './agent-teller.js';
import { AgentNameProvider } from './agent-name.js';
import { ContentProvider } from './content.js';
import { screenRegistry } from './routing/registry.js';
import {
  canViewScreen,
  fetchSession,
  genesysStateConsoleLine,
  NotAuthenticatedError,
  type InteractionState,
  type Session,
} from './session.js';
import { openLoginPopup } from './auth-popup.js';
import { subscribeUnauthenticated } from './auth-store.js';
import { readEmbedContext, type EmbedContext } from './genesys/embed.js';
import { ensureGenesysLink, linkGenesysViaPopup } from './genesys/auth.js';
import { fetchInteraction } from './genesys/interaction.js';

/**
 * Resolve the effective Online/Offline state (#357/#422/#431).
 *
 * The SERVER session is authoritative: the journey is pinned once at entry and
 * the BFF resolves Online for the life of the call, so it persists across
 * navigation (an agent moving to Customer Search must NOT go Offline — Dylan
 * #422). We therefore prefer `session.state`. `embed.state` (the launch URL
 * captured at mount) is only a BOOTSTRAP hint so the very first screen-pop
 * renders Online immediately, before the session resolves.
 *
 * #431 (bucket 3): reconciliation lets a call that ENDED read Offline (so the
 * agent continues Offline) instead of being pinned Online by the stale mount-time
 * `embed.state`. CRITICAL: we only trust a server `offline` once the journey has
 * been CONFIRMED Online at least once (`hasBeenOnline`). During the call-START
 * settle window — pin posted, but the #270 participant check not yet validated —
 * the server transiently resolves Offline; trusting THAT would land the screen-pop
 * on Customer Search instead of ID&V and bounce the agent off online-only screens.
 * So until we've seen Online, we keep the `embed.state` Online bootstrap; only a
 * server Offline AFTER a confirmed Online is a genuine call-end. Reconcile OFF ⇒
 * the #357 bootstrap fallback is unchanged.
 */
export function resolveEffectiveState(
  session: Pick<Session, 'state' | 'journeyReconcile'>,
  embed: Pick<EmbedContext, 'embedded' | 'state'>,
  journeyReady: boolean,
  hasBeenOnline = false,
): InteractionState {
  if (session.state === 'online') return 'online';
  if (session.journeyReconcile === true && journeyReady && hasBeenOnline) {
    return session.state; // a genuine post-Online downgrade (call ended)
  }
  return embed.embedded ? (embed.state ?? session.state) : session.state;
}

/**
 * Inner app: owns session loading and route gating. Rendered inside
 * `CustomerProvider` so the agent banner can reflect the in-context customer.
 */
function AppInner(): React.JSX.Element {
  const [session, setSession] = useState<Session>();
  const [error, setError] = useState<string>();
  // Embedded (Genesys iframe) + not signed in: we can't redirect the iframe to
  // the Cognito hosted UI (X-Frame-Options), so render a sign-in affordance that
  // opens a popup. `signingIn` guards against double-opening.
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  // #422/#430 — the agent's Genesys token is linked via a POPUP (server-side
  // PKCE), NOT a hidden iframe (embedded in the Genesys script the iframe is a
  // third-party context; its cookies are blocked so the link can't complete). The
  // "Connect to Genesys" button lives in the banner and shows whenever the app is
  // Genesys-capable but not yet linked — including the OFFLINE Apps surface, so an
  // agent can pre-heat the connection BEFORE going on queue (#430). It is derived
  // from the session (no state needed here); see `showConnectGenesys` below.
  // #357 (ai-review HIGH) — when embedded, hold the routed screens until the
  // journey has been PINNED server-side (`POST /genesys/session-context`
  // resolved). Screens must not make `/session`/servicing calls that read the
  // resolved journey while the pin is still in flight, or a stale/incomplete
  // pin could route them wrong. Non-embedded surfaces never gate (the check
  // below is embed-guarded), so this only affects the Genesys-embedded entry.
  const [journeyReady, setJourneyReady] = useState(false);
  // #357 (ai-review HIGH — fail-closed): if the pin POST fails we must NOT
  // release the gate to servicing routes (a failed CLEAR could leave a stale
  // prior Online pin ⇒ fail-open). Instead show a retry state; `journeyRetry`
  // bumps to re-run the establish effect on Retry.
  const [journeyError, setJourneyError] = useState(false);
  const [journeyRetry, setJourneyRetry] = useState(0);
  // #628 (increment B) — true while a mid-call "Reconnect to Genesys" re-pin is
  // in flight (disables the reconnect button so it can't be double-fired).
  const [reconnecting, setReconnecting] = useState(false);
  const { customer, setCustomer, updateCustomer } = useCustomer();
  const navigate = useNavigate();
  // Issue #669 (J & N Hold Handling — @dylan219, 2026-09-01: "remove this
  // button and do not allow the agent navigate off this Warning for J&N block
  // only for now"). A J+N-hold verdict published to `jn-hold-block-store` (by
  // the Dashboard's synchronous accounts evaluation or the registration
  // screens' ID&V hold check) renders the NON-DISMISSIBLE `JnHoldPopup`
  // IN PLACE OF the routed screens: while a block raised for the CURRENT
  // customer is active, the agent sees the fraud-team-transfer warning on ANY
  // route with no acknowledge/close affordance and no navigation targets at
  // all — deep links or route changes still render only the warning. Keyed to
  // the customer's CIF (same keying as the server-side block) so a stale
  // verdict for a previous customer can never lock a new identity; cleared on
  // a new ID&V / Clear Session, mirroring when `requireNoJnHoldBlock` drops
  // the server block. The server-side 403 freeze stays the authoritative
  // backstop — this lock is the agent-facing UX, never the enforcement. The
  // banner's Clear Session remains reachable (outside the warning panel) as
  // the ONLY sanctioned escape — the same "covered shell + server-side
  // resolution" precedent as the CCF-13 freeze (#506). Scope is J&N ONLY: the
  // CCF-43 auth-hold pop-up keeps its #574 acknowledge behaviour.
  const [jnBlock, setJnBlock] = useState<JnHoldBlock | null>(getJnHoldBlock());
  useEffect(() => subscribeJnHoldBlock(setJnBlock), []);
  const jnLock =
    jnBlock && customer.cif && jnBlock.customerNumber === customer.cif
      ? jnBlock
      : null;
  // Admin Console banner (#302 follow-up, 2026-07-09 — Dylan: strip the
  // banner down to the logo ONLY on `/admin`; every other screen keeps the
  // full customer-context banner). Read the route here (App.tsx is rendered
  // inside main.tsx's <BrowserRouter>, so `useLocation` is safe) rather than
  // inside `AppLayout`, so `AppLayout`'s existing unit tests — which render it
  // standalone, outside a Router — are unaffected.
  const location = useLocation();
  const isAdminConsole = location.pathname === '/admin';
  // Capture the Genesys launch context ONCE at mount, from the INITIAL URL.
  // The screen-pop URL carries `?gcInteractionId=…`, but the app then
  // client-side navigates to `/idv` (landOnIdv) which drops the query string —
  // so reading the live URL later would lose the interaction id. Freeze it here.
  const [embed] = useState(() => readEmbedContext());
  // Enrich the banner from Genesys at most ONCE per interaction (the effect can
  // re-run on re-render; without this it would re-fetch on every render and spam
  // the Genesys API).
  const enrichedFor = useRef<string>();
  // Latest customer context, read INSIDE the enrichment effect to decide whether
  // this is a new interaction (the #243 prior-call reset). Held in a ref — NOT
  // the effect's dependency array — so the reset's own `updateCustomer` (which
  // mutates these very fields) cannot re-run the effect and cancel its in-flight
  // interaction fetch before it happens (#244: after #243 shipped, the O24
  // number / PAN stopped coming through on live calls because the enrichment was
  // cancelled at the `await`). The effect still only runs once per interaction
  // (guarded by `enrichedFor`), so reading the persisted values via the ref is
  // equivalent to reading them at first run.
  const customerRef = useRef(customer);
  customerRef.current = customer;
  // #357 — one-shot guard for the journey-establish effect, keyed by the
  // journey (the live interaction id, or '' when Offline). Prevents the effect
  // from re-posting when its own `loadSession()` refresh changes `session`.
  const journeyEstablishedFor = useRef<string>();
  // #431 (bucket 3) — latches once the SERVER has confirmed the journey Online
  // (session.state === 'online'). resolveEffectiveState only trusts a server
  // Offline (a call-end downgrade) AFTER this is set, so the call-START settle
  // window keeps the embed Online bootstrap (screen-pop lands on ID&V, no bounce).
  const hasBeenOnlineRef = useRef(false);

  // #716 — remembers the last degraded-online-state console line we emitted so
  // the ~7s session poll logs it ONCE per distinct cause (not every poll). The
  // network response always carries the fields regardless; this is the console
  // half of dylan219's "push visibility to the network/console logs".
  const lastGenesysStateLineRef = useRef<string | undefined>(undefined);

  const loadSession = React.useCallback(() => {
    fetchSession()
      .then((s) => {
        // #716 — surface WHY the server resolved a degraded/unverified Online
        // claim (banner may show Online while every action is treated Offline)
        // to the browser console, deduped by the line so a live-call capture
        // names the cause (e.g. `fetchStatus=403` = agent Genesys-role gap).
        const line = genesysStateConsoleLine(s);
        if (line && line !== lastGenesysStateLineRef.current) {
          lastGenesysStateLineRef.current = line;
          console.warn(line);
        } else if (!line) {
          lastGenesysStateLineRef.current = undefined;
        }
        // #431 — IDEMPOTENT update: only replace the session object when its
        // CONTENT actually changed. The bucket-1 re-validation (focus/visibility)
        // and bucket-3 reconcile poll call loadSession repeatedly; if each
        // returned a NEW object identity it would re-render and CANCEL the
        // in-flight Genesys call-context enrichment (the #244 race the journey
        // effect comment warns about) — leaving the banner N/A on a live call.
        // Returning the previous reference on an unchanged poll bails the render,
        // so enrichment is never interrupted; a REAL change (logout, online→
        // offline downgrade) still swaps the object and propagates.
        setSession((prev) =>
          prev && JSON.stringify(prev) === JSON.stringify(s) ? prev : s,
        );
        setNeedsSignIn(false);
      })
      .catch((err: unknown) => {
        if (err instanceof NotAuthenticatedError) {
          // Embedded + unauthenticated → show the popup sign-in affordance.
          // #431 (bucket 1): also DROP any cached session so a re-validation
          // (focus/visibility/401) that discovers a mid-session logout tears the
          // authed UI down instead of leaving it stale behind the sign-in gate.
          setSession(undefined);
          setNeedsSignIn(true);
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load session');
      });
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // #431 (bucket 1) — RE-VALIDATE the session when the agent returns to the app
  // (tab focus / visibility) and the moment any authenticated call 401s. The SPA
  // otherwise caches the mount-time session forever, so a logout in another tab
  // (the shared cookie is destroyed) stays invisible until a hard refresh. The
  // server remains the sole authority — this only reconciles the client to it.
  useEffect(() => {
    const revalidate = (): void => {
      if (document.visibilityState === 'visible') loadSession();
    };
    const onFocus = (): void => loadSession();
    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', onFocus);
    const unsubscribe = subscribeUnauthenticated(() => {
      // A 401 from any authenticated call: re-confirm via the session endpoint,
      // which flips to the sign-in affordance (embedded) or redirects (standalone).
      loadSession();
    });
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', onFocus);
      unsubscribe();
    };
  }, [loadSession]);

  // #431 (bucket 2) — AUTO-POP the Cognito/Entra sign-in on entry when embedded
  // and signed out, so the agent isn't forced to click first. ADDITIVE: if the
  // browser blocks the (now gesture-less) popup, we swallow the error and the
  // "Sign in" button remains as the fallback. One attempt per sign-out episode.
  const autoSignInTried = useRef(false);
  useEffect(() => {
    if (!needsSignIn) {
      autoSignInTried.current = false;
      return;
    }
    if (!embed.embedded || autoSignInTried.current) return;
    autoSignInTried.current = true;
    void (async () => {
      try {
        await openLoginPopup();
        loadSession();
      } catch {
        /* popup blocked → the Sign in button covers it */
      }
    })();
  }, [needsSignIn, embed.embedded, loadSession]);

  // #431 (bucket 2) — AUTO-POP the Genesys "Connect" ONLY when on a live (Online)
  // journey and not yet linked, so an incoming call self-connects. Offline keeps
  // the calm #430 pre-heat BUTTON (no auto-pop). ADDITIVE: a blocked popup just
  // leaves the pulsing Connect button (no toast on the auto path). Once per call.
  const autoLinkTriedFor = useRef<string>();
  useEffect(() => {
    if (!session || !embed.embedded) return;
    const online = session.state === 'online' || embed.state === 'online';
    if (!online || session.genesysLinked === true) return;
    const key = embed.params.interactionId ?? 'online';
    if (autoLinkTriedFor.current === key) return;
    autoLinkTriedFor.current = key;
    void (async () => {
      const ok = await linkGenesysViaPopup();
      if (ok) {
        enrichedFor.current = undefined;
        loadSession();
      }
      /* blocked/declined → the pulsing Connect button remains the fallback */
    })();
  }, [
    session,
    embed.embedded,
    embed.state,
    embed.params.interactionId,
    loadSession,
  ]);

  // #431 (bucket 3) — while reconciliation is on + embedded, poll /session so a
  // server-side journey downgrade (the call ended) surfaces WITHOUT waiting for a
  // refocus. Cheap (a session read); paused when the tab is hidden. Flag-gated
  // (GENESYS_JOURNEY_RECONCILE) so it is instantly revertable and OFF by default.
  useEffect(() => {
    if (session?.journeyReconcile !== true || !embed.embedded) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadSession();
    }, 15_000);
    return () => clearInterval(id);
  }, [session?.journeyReconcile, embed.embedded, loadSession]);

  // #431 (bucket 3) — RE-GATE the current screen ONLY on a genuine ONLINE→OFFLINE
  // transition (the call ended). We must NOT re-gate during the call-START settle
  // (a transient pre-validation Offline) — that would bounce the agent off the
  // ID&V screen-pop to Customer Search and it would never come back. So we act
  // only on the edge where the effective state was Online on a prior render and is
  // now Offline. `hasBeenOnlineRef` (set in render) makes resolveEffectiveState
  // ignore the settle-window Offline, so `state` here can only be Offline after a
  // confirmed Online. Flag-gated on reconcile.
  const prevEffectiveRef = useRef<InteractionState>();
  useEffect(() => {
    if (!session || session.journeyReconcile !== true) {
      prevEffectiveRef.current = undefined;
      return;
    }
    const state = resolveEffectiveState(
      session,
      embed,
      journeyReady,
      hasBeenOnlineRef.current,
    );
    const prev = prevEffectiveRef.current;
    prevEffectiveRef.current = state;
    if (prev !== 'online' || state !== 'offline') return; // only the call-end edge
    const current = screenRegistry.find((s) => s.path === location.pathname);
    if (!current) return;
    const view = { ...session, state };
    if (!canViewScreen(view, current.id)) {
      navigate(canViewScreen(view, 'SCRN-02') ? '/customer-search' : '/', {
        replace: true,
      });
    }
  }, [session, embed, journeyReady, location.pathname, navigate]);

  // Open the Cognito hosted UI in a popup (embedded surface), then re-check the
  // session once it completes (the BFF set the shared session cookie).
  const signIn = React.useCallback(async () => {
    if (signingIn) return;
    setSigningIn(true);
    try {
      await openLoginPopup();
      loadSession();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setSigningIn(false);
    }
  }, [signingIn, loadSession]);

  // #422 — link the agent's Genesys token via a first-party POPUP (server-side
  // PKCE). Called from a user gesture (the Connect affordance below). On success
  // the BFF session holds the token: re-arm the enrichment (clear `enrichedFor`)
  // and reload the session so the effect re-runs and pulls the call context
  // (CLI, calls-in-last-week, and the IVR O24/PAN hints).
  const linkGenesys = React.useCallback(async () => {
    const ok = await linkGenesysViaPopup();
    if (ok) {
      // Linked: re-arm enrichment and reload the session so `genesysLinked` flips
      // true — the banner Connect button then hides on its own.
      enrichedFor.current = undefined;
      loadSession();
    } else {
      toast.error(
        'Could not connect to Genesys. Allow the popup for this site and try again.',
      );
    }
  }, [loadSession]);

  // #628 (increment B) — mid-call "Reconnect to Genesys" recovery. When an
  // online-only action was refused SOLELY because the Genesys online link dropped
  // mid-call (`onlineWouldAllow` on the 403, captured into the reconnect store),
  // the agent clicks Reconnect and we RE-PIN the interaction id server-side.
  //
  // The interaction id is re-sourced from the MOUNT-FROZEN embed context
  // (`embed.params.interactionId`), NOT a fresh URL read: the screen-pop drops the
  // `?gcInteractionId` query string after landing on /idv, so the live URL no
  // longer carries it — but we captured it once at mount (see the `embed` state).
  //
  // SECURITY: this re-pins through the EXISTING `POST /genesys/session-context`
  // route, which runs the SAME server-side #270 participant validation (resolve
  // the agent via Genesys `/users/me`, verify they are a live participant of the
  // conversation) before the session resolves Online again. We do NOT trust the
  // client id: a successful POST only PINS it; the very next `resolveClaim` still
  // re-validates it as a participant. This is a RECOVERY path, never a bypass — a
  // failed re-validation keeps the session Offline and the banner in place.
  const handleReconnect = React.useCallback(async () => {
    if (reconnecting) return;
    const interactionId = embed.params.interactionId;
    if (!interactionId) {
      // No interaction id to re-pin (e.g. launched without a live interaction) —
      // reconnect is not applicable. Fail closed: leave the banner, tell the agent.
      toast.error(
        'No live Genesys interaction to reconnect to. Ensure the call is active in Genesys.',
      );
      return;
    }
    setReconnecting(true);
    try {
      // #837 (option B) — when the online link dropped because the Genesys TOKEN
      // is not linked (`pinned-token-not-linked`, or `no-token`), re-pinning the
      // interaction ALONE can never recover it: the server has no token to
      // participant-validate against, so the re-pin re-resolves Offline for the
      // SAME reason and the agent is stuck ("nothing I click works"). For those
      // reasons we must first force the Genesys TOKEN to (re)link. This handler
      // runs from the Reconnect-button CLICK, so the user gesture a popup needs is
      // present; the popup auto-completes + closes sub-second when the agent's
      // Genesys IdP session is still alive (no credential re-entry). Other reasons
      // (e.g. `not-a-participant` — token present, wrong/stale pin) are genuine
      // re-pin cases and keep the existing re-pin-only path.
      const reason = getReconnectNeeded()?.offlineReason;
      if (reason === 'pinned-token-not-linked' || reason === 'no-token') {
        const linked = await linkGenesysViaPopup();
        if (!linked) {
          // Token re-link failed (popup blocked / IdP session gone). Fail closed:
          // keep the banner so the agent can retry, and point them at the blocker.
          toast.error(
            'Could not reconnect to Genesys. Allow the popup for this site and make sure your Genesys session is active, then try again.',
          );
          return;
        }
      }
      // Re-post the interaction id: the server re-validates (#270) then re-pins it.
      const { error: postError } = await api.POST('/genesys/session-context', {
        body: { interactionId },
      });
      if (postError) throw new Error('reconnect_post_failed');
      // The re-pin succeeded server-side. Clear the reconnect prompt and re-arm
      // enrichment, then reload the session so the resolved state re-reads. If the
      // #270 re-validation did NOT actually confirm the agent as a participant the
      // server stays Offline and the very next online-only action will re-raise
      // the banner — we never optimistically grant Online here.
      setReconnectNeeded(null);
      // Force the journey-establish + enrichment effects to re-run for this id.
      journeyEstablishedFor.current = undefined;
      enrichedFor.current = undefined;
      loadSession();
      toast.success('Reconnected to Genesys.');
    } catch {
      // Fail closed: keep the banner (do NOT clear the store) and tell the agent.
      toast.error(
        'Could not reconnect to Genesys. Check the call is still active and try again.',
      );
    } finally {
      setReconnecting(false);
    }
  }, [reconnecting, embed.params.interactionId, loadSession]);

  // #357 — establish the Online/Offline JOURNEY once at entry. When embedded in
  // Genesys, tell the BFF the current live interaction id (Online) or clear it
  // (out-of-call/standalone ⇒ Offline). The server then resolves the journey
  // from the session, so no screen needs to forward `X-Genesys-Interaction-Id`
  // per call (fixes #340, and #342's stale-id mis-route to Medium Security).
  // Not embedded (local/demo) ⇒ leave the server's mock resolver in charge.
  // Fire-and-forget + one-shot per journey (the ref guards against a re-post if
  // `session` changes). We deliberately do NOT force a /session re-read here: it
  // would re-render and cancel the in-flight call-context enrichment below (the
  // #244 late-token case). It is not needed for correctness either — the POST
  // PINS the journey server-side, so every subsequent /session + screen call
  // resolves authoritatively; screens re-read /session on their own mount.
  useEffect(() => {
    if (!session) return; // authenticated only
    if (!embed.embedded) return;
    const interactionId =
      embed.state === 'online' ? (embed.params.interactionId ?? '') : '';
    if (journeyEstablishedFor.current === interactionId) return;
    journeyEstablishedFor.current = interactionId;
    void (async () => {
      try {
        const { error: postError } = await api.POST(
          '/genesys/session-context',
          { body: interactionId ? { interactionId } : {} },
        );
        if (postError) throw new Error('session_context_post_failed');
        setJourneyReady(true);
      } catch {
        // FAIL CLOSED on ANY failure (non-2xx OR network): do NOT release the
        // gate to servicing routes — a failed CLEAR could leave a stale prior
        // Online pin (fail-open). Show a retry state instead, and reset the
        // one-shot guard so Retry re-posts.
        journeyEstablishedFor.current = undefined;
        setJourneyError(true);
      }
    })();
  }, [session, embed, journeyRetry]);

  // Genesys call-context enrichment — the ONLY Genesys use, and NOT a gate.
  // Runs lazily ONLY when embedded in the Genesys desktop on an active (Online)
  // interaction AND the BFF advertised the public PKCE config. The Genesys
  // access token is obtained on demand (PKCE) only here. For non-Genesys users
  // / offline surfaces this is a no-op and the app renders fully via the OIDC
  // session — Genesys is never required to use the app.
  useEffect(() => {
    if (!session?.genesys) return;
    if (!embed.embedded || embed.state !== 'online') return;
    const conversationId = embed.params.interactionId;
    if (!conversationId) return;
    if (enrichedFor.current === conversationId) return; // once per interaction
    enrichedFor.current = conversationId;

    // #243 + ai-review fail-closed — a NEW live interaction must start ID&V
    // from a CLEAN slate, and this reset MUST run SYNCHRONOUSLY here, BEFORE we
    // await Genesys enrichment. If `ensureToken()` / `fetchInteraction()` reject
    // for a new conversation, a prior call's identified customer (cif / name /
    // online card / **verification level**) must NOT linger in the banner for
    // the new caller — that is a cross-customer data exposure. Clearing it only
    // on the successful path (as before) failed OPEN on enrichment failure.
    // Detect a new interaction by the conversation id the current context
    // belongs to (`contextConversationId`, stamped on success below and
    // preserved through identification) differing from the one now being
    // enriched, and clear the identified customer + any stale IVR hints up
    // front, so ID&V restarts clean on BOTH the success and the failure path. A
    // reload of the SAME call keeps the identity (ids match). This is
    // client-side context hygiene only — the BFF still re-verifies + binds each
    // servicing call to the verified customer.
    // #837 — NEVER wipe the verified identity on a TRANSIENT / unresolved
    // INCOMING conversation id. The link-window bug (see the server-side Layer 1
    // fix) showed the identity (name + the CIF the mobile-number lookup is keyed
    // to) blanking while the Genesys token linked. That protection is already
    // provided STRUCTURALLY by the early-return above (`if (!conversationId)
    // return;`): a transient/empty incoming id never reaches this wipe, and the
    // once-per-interaction guard (`enrichedFor`) means this decision runs at most
    // once per live call and can only ever clear a PRIOR loaded customer — never
    // one identified during the current call. So the real #837 boundary does not
    // depend on the wipe condition at all.
    //
    // #837 regression fix: the original #837 client leg ALSO required the prior
    // context id to be a non-empty string (`priorConvId.length > 0`) before
    // wiping. That opened a cross-customer hole: a customer identified on a call
    // whose enrichment never stamped the conversation (the token-link window)
    // carries an EMPTY `contextConversationId`, so on the next, genuinely-new
    // call the wipe was SKIPPED and the prior customer lingered for the new caller
    // — the exact exposure reported (agent sends a customer to IVR to register,
    // the next call shows the same details). So wipe whenever the current,
    // confirmed non-empty conversation id differs from the context the loaded
    // customer belongs to — INCLUDING when that prior stamp is empty/unset (an
    // unstamped prior customer does NOT belong to this live call). A reload of
    // the SAME call keeps the identity (ids match). This restores the #243
    // cross-customer protection.
    const prior = customerRef.current;
    const priorConvId = prior.contextConversationId;
    const isConfirmedNewCaller =
      typeof conversationId === 'string' &&
      conversationId.length > 0 &&
      priorConvId !== conversationId;
    if (
      isConfirmedNewCaller &&
      (prior.cif || prior.name || prior.onlineCardNumber)
    ) {
      updateCustomer({
        name: undefined,
        onlineCardNumber: undefined,
        cif: undefined,
        assistanceRequired: undefined,
        customerVerification: undefined,
        cliMatch: undefined,
        dob: undefined,
        segment: undefined,
        registrationType: undefined,
        securityLevel: undefined,
        deregistered: undefined,
        idvJourney: undefined,
        // Drop a prior call's IVR hints too — this interaction's enrichment
        // (below, on success) re-establishes them bound to THIS conversation id.
        ivrOnlineCardNumber: undefined,
        ivrPanVerified: undefined,
        ivrConversationId: undefined,
      });
    }

    let cancelled = false;
    void (async () => {
      try {
        // #343/#422: the BFF must hold a Genesys token for this session to read
        // the interaction (server-side PKCE; the browser never holds the token).
        // The link happens via a POPUP that needs a user gesture, so we cannot
        // auto-link here. If not yet linked, stop — the banner "Connect to
        // Genesys" button (shown whenever unlinked) prompts the agent; enrichment
        // re-runs after they link (linkGenesys resets `enrichedFor` + reloads).
        const linked = await ensureGenesysLink(session.genesysLinked === true);
        if (cancelled || !linked) return;
        const ctx = await fetchInteraction(conversationId);
        if (cancelled) return;
        // PRE-AUTH banner enrichment, per CCF-2/CCF-3: only the call-derived
        // fields are populated from Genesys — the **CLI (the caller's number)**
        // and **"calls in the last week"** (a Genesys analytics count for that
        // number). The customer's NAME/CIF are NOT taken from the call (a real
        // PSTN call carries only the number, no name) — they come from
        // `/customer/info` once the agent captures the card (ID&V). The CLI is
        // kept so CLI-match can be computed against the identified customer.
        //
        // #201 (ONLINE journey) — the IVR may already have captured the
        // customer's Online Card Number (`Open24Nr`) and verified their PAN
        // (`Verification=PAN`). Stash both from the conversation attributes so
        // the ID&V screen can auto-run the card-lookup chain and (when the PAN
        // was IVR-verified) skip straight to Medium Security. These are only
        // HINTS — the BFF re-confirms the PAN signal server-side before it
        // raises the verification level (IDOR-safe).
        // Read the IVR conversation attributes case-INSENSITIVELY on the key
        // (#201): Genesys attribute keys can arrive with different casing than
        // the `Open24Nr`/`Verification` names configured in the flow, and an
        // exact-case miss would silently drop the hint (no auto-lookup / no
        // PAN-skip). Values are still compared as documented (`Verification=PAN`,
        // case-insensitive on the value too).
        const attrs = ctx.attributes ?? {};
        const attr = (name: string): string | undefined => {
          const key = Object.keys(attrs).find(
            (k) => k.toLowerCase() === name.toLowerCase(),
          );
          return key ? attrs[key]?.trim() : undefined;
        };
        const open24 = attr('Open24Nr');
        const ivrPanVerified = attr('Verification')?.toUpperCase() === 'PAN';
        // Set the IVR hints EXPLICITLY (to `undefined` when the current
        // conversation carries none), BOUND to this conversation id, and stamp
        // the conversation this context now belongs to. The prior call's
        // identity was already cleared synchronously up front (the #243 /
        // fail-closed reset), so a NEW interaction is guaranteed clean here on
        // the success path. The ID&V screen auto-triggers ONLY when the stored
        // `ivrConversationId` matches the live interaction, so a prior call's
        // hints can never drive a lookup on a different call (defence-in-depth
        // alongside the upfront clear and the server-side card-binding).
        updateCustomer({
          cli: ctx.ani,
          callsInLastWeek: ctx.callsInLastWeek,
          ivrOnlineCardNumber: open24 || undefined,
          ivrPanVerified: open24 && ivrPanVerified ? true : undefined,
          ivrConversationId: open24 ? conversationId : undefined,
          // Stamp the conversation this context now belongs to, so a reload of
          // this same call is NOT treated as a new interaction (no reset).
          contextConversationId: conversationId,
        });
      } catch (err) {
        if (cancelled) return;
        // Non-fatal: the app stays Online but without enriched banner context.
        // Logged for live diagnosis inside the Genesys iframe.
        //
        // #201 — a FAILED enrichment on THIS interaction must not leave a PRIOR
        // call's IVR hints behind (which could otherwise auto-trigger a lookup
        // for the previous customer's card). Clear them here so a failed read
        // never inherits stale hints. (The ID&V screen also binds the auto-
        // trigger to the live conversation id, so this is defence-in-depth.)
        updateCustomer({
          ivrOnlineCardNumber: undefined,
          ivrPanVerified: undefined,
          ivrConversationId: undefined,
        });
        console.warn('[genesys] interaction enrichment failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Deps are intentionally MINIMAL and STABLE (`session` settles once,
    // `updateCustomer` is a stable `useCallback`, `embed` is frozen at mount) so
    // the effect runs at most once per interaction. The prior-call identity used
    // for the #243 reset is read from `customerRef.current` — deliberately NOT a
    // dependency — so the reset's own `updateCustomer` cannot re-trigger this
    // effect and cancel the in-flight interaction fetch (the #244 race).
  }, [session, updateCustomer, embed]);

  async function clearSession(): Promise<void> {
    // CCF-10 "Clear Session": clear the CUSTOMER's authentication session so the
    // agent can re-authenticate with alternative credentials — this must NOT log
    // the agent out of the app/Genesys. It releases the server-side ID&V /
    // verification context (and the CCF-13 duplicate-login hold) via the BFF —
    // so servicing routes are denied until a fresh ID&V — then clears the local
    // customer banner context and sends the agent back to the ID&V screen with a
    // blank slate. The agent's OIDC session is left intact.
    //
    // FAIL CLOSED: the local reset + navigation happen ONLY after the BFF
    // confirms the server-side clear-down (2xx). If the call is rejected,
    // unavailable, or RBAC-denied we surface an error and keep the agent in
    // place — never present a blank, "cleared" UI while the previous customer's
    // verification context (and duplicate-login hold) is still live server-side.
    const tellerId = session?.tellerId;
    if (!tellerId) {
      // Without a Teller ID we cannot clear the session server-side; refuse
      // rather than fake a local-only clear (which would leave the server state
      // unchanged).
      toast.error('Cannot clear session: no Teller ID for this agent.');
      return;
    }
    try {
      const { error, response } = await api.POST('/idv/clear-session', {
        params: { header: { 'X-Teller-Id': tellerId } },
      });
      if (error || !response.ok) {
        // CCF-10: the 4th clear-down in one interaction is refused server-side
        // (429) with a fixed limit message. Surface that exact message; the
        // session is intentionally NOT cleared and the agent stays in place.
        if (response.status === 429) {
          const limitMessage =
            (error as { message?: string } | undefined)?.message ??
            'Clear session max limit has been reached: please advise the customer they will have to call in again due to max tries being reached.';
          toast.error(limitMessage);
          return;
        }
        toast.error('Clear session failed — please retry.');
        return;
      }
    } catch (err) {
      console.warn('[clear-session] server-side release failed', err);
      toast.error('Clear session failed — please retry.');
      return;
    }
    // #251 (ONLINE journey) — Clear Session must reset the CUSTOMER / ID&V
    // context but PRESERVE the online journey when a live Genesys interaction is
    // in progress. Offline (no live call) is determined SOLELY by the absence of
    // a conversation id, so it stays fully reset (`clearCustomer()`), but online
    // it must NOT revert to offline just because the customer was cleared: the
    // journey stays online and the SAME conversation id keeps being used as the
    // correlation id for the next customer looked up against this call (so Call
    // History still logs against that conversation id). The `effectiveState` that
    // drives Online/Offline reads from the frozen `embed` context (not the
    // customer context), so the journey state itself already survives the clear;
    // the bug (#251) is that `clearCustomer()` also wiped the frozen conversation
    // id anchors (`contextConversationId` / `ivrConversationId`), dropping the id
    // the SPA forwards (`X-Genesys-Interaction-Id`) to keep the gateway/Call
    // History correlated to the live call. Re-establishing them from the frozen
    // launch context keeps that continuity without re-reading the (query-string-
    // dropped) live URL. IVR PAN/O24 HINTS are intentionally NOT preserved — a
    // Clear Session means the customer re-authenticates from scratch, so the
    // enrichment/screen must not auto-skip using the previous authentication's
    // hints; only the correlation anchors survive.
    // #681 (CCF-10): bump a clear-session nonce carried in the customer context
    // on EVERY clear-down (both paths below). The ID&V screen watches it and
    // resets its LOCAL identification inputs (the Open24 online-card number and
    // the alternative Sort Code + account number fields) to empty — clearing the
    // customer context alone does not touch those inputs, so before this they
    // stayed populated after a Clear Session. Derived from the current value so
    // it strictly increases across successive clear-downs in the same session.
    const nextClearSessionNonce = (customer.clearSessionNonce ?? 0) + 1;
    const liveConversationId =
      embed.embedded && embed.state === 'online'
        ? embed.params.interactionId
        : undefined;
    if (liveConversationId) {
      // Preserve ONLY the correlation anchors + the live-call banner context
      // (CLI / calls-in-last-week are properties of the call, not the customer),
      // dropping every identity / verification / IVR-hint field. This is
      // equivalent to a fresh customer context on the SAME live interaction.
      // `resetIdentityPreservingOnlineJourney` keys off the persisted
      // `contextConversationId`; anchor it to the live interaction id here so
      // the reset preserves it even if the enrichment effect has not restamped.
      setCustomer({
        ...resetIdentityPreservingOnlineJourney({
          ...customer,
          contextConversationId: liveConversationId,
        }),
        clearSessionNonce: nextClearSessionNonce,
      });
    } else {
      // Offline full-reset: blank slate, but still stamp the nonce so the ID&V
      // screen clears its input fields (a bare `clearCustomer()` → `{}` would
      // drop the signal).
      setCustomer({ clearSessionNonce: nextClearSessionNonce });
    }
    // CCF-13 (#413): a successful Clear Session lifts the duplicate-session
    // freeze server-side, so clear the client-side overlay too.
    setDuplicateSessionBlock(null);
    // #669: the server clears its `requireNoJnHoldBlock` on the same
    // clear-down, so drop the client-side navigation lock here too (the next
    // ID&V re-derives the verdict server-side — the lock only ever lives as
    // long as the server block).
    setJnHoldBlock(null);
    // #780: wipe any in-progress security-run snapshot(s). The security screens
    // persist the agent's answers to sessionStorage so a run survives a same-tab
    // round trip; on an online journey Clear Session keeps the SAME conversation
    // id (#251, above), and the snapshot restore is keyed on that id, so without
    // this a re-entry to a security screen (e.g. Mortgage Security) for the same
    // customer re-populated the previous, now-stale answers. A clear-down means
    // the customer re-authenticates from scratch, so the security screens must
    // start blank.
    clearAllSecurityRunSnapshots();
    // #790: also wipe any retained registration answer snapshots (including the
    // retained "reason for non-completion"). Retention is scoped to navigation
    // within the same session; a clear-down is a hard re-authentication, so the
    // registration screens must start blank — the same rule as the security-run
    // snapshots above.
    clearAllRegistrationAnswers();
    navigate('/idv');
    toast.success('Session cleared — re-authenticate the customer.');
  }

  // Sync adapter for the banner button's `() => void` prop — fire the async
  // clear-down without leaking a floating promise.
  const handleClearSession = (): void => {
    void clearSession();
  };

  if (error) {
    return <div className="fatal">Unable to start: {error}</div>;
  }

  // Embedded (Genesys iframe) + not signed in: render the popup sign-in
  // affordance. An in-iframe redirect to the Cognito hosted UI is blocked by
  // X-Frame-Options, so the agent signs in through a popup window instead.
  if (needsSignIn) {
    return (
      <AppLayout
        session={undefined}
        onClearSession={handleClearSession}
        logoOnly={isAdminConsole}
      >
        <div className="landing">
          <h1>PTSB Agent Desktop</h1>
          <p>Please sign in to continue.</p>
          <button type="button" onClick={signIn} disabled={signingIn}>
            {signingIn ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </AppLayout>
    );
  }

  // Wait for the session before rendering the router. Otherwise a deep link
  // (e.g. /idv) would hit the permission guards while `session` is still
  // undefined and be bounced to "/" before the session resolves.
  if (!session) {
    return (
      <AppLayout
        session={undefined}
        onClearSession={handleClearSession}
        logoOnly={isAdminConsole}
      >
        <div className="landing">
          <p>Loading session…</p>
        </div>
      </AppLayout>
    );
  }

  // #357 (ai-review HIGH) — embedded in Genesys: hold the routed screens until
  // the journey is PINNED server-side, so no screen reads the resolved
  // Online/Offline journey while the pin POST is still in flight (which could
  // route/gate on a stale pin). Brief, and only on the embedded entry; the
  // enrichment effect above keeps running underneath (it does not depend on
  // this state, so releasing the gate never cancels an in-flight enrichment).
  if (embed.embedded && journeyError) {
    // FAIL CLOSED: the pin could not be established — do not render servicing
    // routes (a stale prior pin could otherwise route Online). Offer a retry.
    return (
      <AppLayout
        session={undefined}
        onClearSession={handleClearSession}
        logoOnly={isAdminConsole}
      >
        <div className="landing" role="alert">
          <p>Could not establish the Genesys call context.</p>
          <button
            type="button"
            onClick={() => {
              setJourneyError(false);
              setJourneyRetry((n) => n + 1);
            }}
          >
            Retry
          </button>
        </div>
      </AppLayout>
    );
  }
  if (embed.embedded && !journeyReady) {
    return (
      <AppLayout
        session={undefined}
        onClearSession={handleClearSession}
        logoOnly={isAdminConsole}
      >
        <div className="landing">
          <p>Connecting to Genesys…</p>
        </div>
      </AppLayout>
    );
  }

  // The single auth gate is the OIDC session (mock IdP in dev, Cognito in
  // staging, Entra in production) — established before this renders (fetchSession
  // redirects to /auth/login on 401). Genesys is NOT a gate: roles/users without
  // Genesys access the app fully. See docs/genesys-integration.md.

  // Online/Offline resolution (#357/#422/#431) — see resolveEffectiveState.
  // Latch "confirmed Online" so a later server Offline is treated as a genuine
  // call-end downgrade, never the call-start settle window.
  if (session.state === 'online') hasBeenOnlineRef.current = true;
  const effectiveState = resolveEffectiveState(
    session,
    embed,
    journeyReady,
    hasBeenOnlineRef.current,
  );

  // The banner reflects the live customer context held in CustomerProvider and
  // the effective (embed-derived when embedded) Online/Offline state.
  const bannerSession = { ...session, state: effectiveState, customer };

  // #430 — the banner "Connect to Genesys" button. Shown whenever the app is
  // Genesys-capable (`session.genesys` config present) and the BFF is not yet
  // linked — INCLUDING the offline Apps surface, so an agent can pre-heat the
  // connection before going on queue. It only PULSES on a live (Online)
  // interaction with no link yet (a call needs enrichment now); offline it is a
  // calm, steady button. Hidden once linked (and on the logo-only Admin banner).
  const showConnectGenesys =
    !!session.genesys && session.genesysLinked !== true;
  const connectGenesysPulse = showConnectGenesys && effectiveState === 'online';

  // Screen-pop landing: when Online (an interaction is active — i.e. a call has
  // arrived) the agent should land directly on the ID&V / Customer Verification
  // screen instead of the generic home landing, matching the design. We only do
  // this when the role is permitted to view SCRN-01 (RBAC, never navigate to a
  // denied screen). This drives ONLY the `/` (root) route element below, so it
  // affects the initial landing exclusively and never fights manual navigation
  // once the agent moves elsewhere in the app.
  const landOnIdv =
    effectiveState === 'online' && canViewScreen(bannerSession, 'SCRN-01');

  // Offline (no live call) landing: there is no dedicated home/landing screen.
  // Per CCF-105 the agent's offline starting point is the Customer Search screen
  // (SCRN-02, `/customer-search`), which is granted to all agent roles. So when
  // we are NOT screen-popping to ID&V, land on Customer Search — but ONLY when
  // the role is permitted to view it (RBAC, deny-by-default: never navigate to a
  // denied screen). Any role that cannot view Customer Search keeps the generic
  // placeholder landing below. Mirrors the landOnIdv pattern.
  const landOnCustomerSearch =
    !landOnIdv && canViewScreen(bannerSession, 'SCRN-02');

  // #715 — discrete Log out affordance: only on ID&V and Customer Search pages
  // (the natural entry points for a session), where an agent may want to switch
  // accounts without navigating away. Calls GET /auth/logout which destroys the
  // server-side session and performs RP-initiated logout at the IdP.
  const showLogout =
    location.pathname === '/idv' || location.pathname === '/customer-search';
  function handleLogout(): void {
    window.location.href = '/auth/logout';
  }

  // Display-only identity line — driven by the authenticated OIDC session (UPN +
  // RBAC role). Genesys is not an identity source; it only enriches the live-call
  // banner (above) when embedded in the Genesys desktop on a call.
  const signedInAs = `Signed in as ${session.upn} (${session.role}).`;

  return (
    <AgentTellerProvider tellerId={session.tellerId}>
      <AgentNameProvider agentName={session.agentName}>
        {/* CCF-13 (#413) — duplicate ONLINE session freeze. Rendered ABOVE the
          shell so it covers every screen: when the BFF signals a duplicate
          session the agent is frozen out of all processes until a Clear Session.
          A no-op (renders nothing) when there is no block. */}
        <DuplicateSessionOverlay onClearSession={handleClearSession} />
        <AppLayout
          session={bannerSession}
          onClearSession={handleClearSession}
          logoOnly={isAdminConsole}
          showConnectGenesys={showConnectGenesys}
          connectGenesysPulse={connectGenesysPulse}
          onConnectGenesys={() => void linkGenesys()}
          onLogout={showLogout ? handleLogout : undefined}
        >
          {/* Issue #669 — J&N NAVIGATION LOCK. While a J+N-hold block raised for
            the CURRENT customer is active, the routed screens are NOT rendered
            at all: the NON-DISMISSIBLE fraud-team-transfer warning replaces
            them, so on ANY route (incl. deep links / programmatic navigations)
            the agent sees the warning and has no navigation target to move to
            (@dylan219, 2026-09-01). The agent banner above (incl. its
            server-side Clear Session — the sanctioned recovery the BFF's
            allow-list keeps open) remains the only interactive surface. */}
          {jnLock ? (
            <JnHoldPopup result={jnLock.result} />
          ) : (
            <>
              {/* #628 (increment B) — mid-call "Reconnect to Genesys" affordance.
            Rendered INSIDE the shell (non-blocking, above the routed screen) so
            the agent can still work while the call is live: when an online-only
            action was refused SOLELY because the Genesys online link dropped
            mid-call, this shows a clear message + a Reconnect button instead of a
            bare permission error. A no-op (renders nothing) when not needed. */}
              <GenesysReconnectBanner
                onReconnect={() => void handleReconnect()}
                reconnecting={reconnecting}
              />
              {/* Route-level error boundary (#276 / #279): an uncaught render throw in
            any screen previously unmounted the whole React tree — a grey/white
            blank page that only a browser refresh recovered, with no way back.
            The boundary lives INSIDE the shell so the app chrome/navigation
            survives, shows a recoverable panel, and clears itself on route
            change (navigate away → recover, no refresh). */}
              <RouteErrorBoundary>
                <Routes>
                  {screenRegistry.map((screen) => (
                    <Route
                      key={screen.id}
                      path={screen.path}
                      element={
                        canViewScreen(bannerSession, screen.id) ? (
                          screen.element
                        ) : (
                          // #248 block-message UX: a page the role may not view no longer
                          // silently redirects home — it renders the permission-denied
                          // page (verbatim copy + a Back button). RBAC stays authoritative.
                          <PermissionDeniedPage />
                        )
                      }
                    />
                  ))}
                  <Route
                    path="/"
                    element={
                      landOnIdv ? (
                        <Navigate to="/idv" replace />
                      ) : landOnCustomerSearch ? (
                        <Navigate to="/customer-search" replace />
                      ) : (
                        <div className="landing">
                          <h1>PTSB Agent Desktop</h1>
                          <p>{signedInAs}</p>
                        </div>
                      )
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </RouteErrorBoundary>
            </>
          )}
        </AppLayout>
      </AgentNameProvider>
    </AgentTellerProvider>
  );
}

/**
 * Root application component. Loads the session and renders permission-gated
 * routes inside the shared app shell, with the customer-context provider and
 * the toast host mounted at the top level.
 *
 * #343: the Genesys PKCE callback (`/auth/genesys/callback`) is now handled
 * SERVER-SIDE by the BFF (it completes the code→token exchange and holds the
 * token in the session), so the SPA no longer intercepts that path.
 */
export function App(): React.JSX.Element {
  return (
    <CustomerProvider>
      <ContentProvider>
        <AppInner />
        <Toaster richColors position="top-right" />
      </ContentProvider>
    </CustomerProvider>
  );
}
